/**
 * Create standard list mutations for a store module
 * Provides reusable mutations for handling list data, pagination, loading state, etc.
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.dataPath - Path to list data in response (default: 'data.list')
 *                                     Can be 'data.data' or any nested path
 * @param {string} options.countField - Field name for total count (default: 'countdata')
 *                                       Can be 'count_data', 'total', etc.
 * @param {string} options.pageField - Field name for total pages (default: 'totalPage')
 *                                      Can be 'total_page', etc.
 */
export function createListMutations(options = {}) {
  const {
    dataPath = 'data.list',
    countField = 'countdata',
    pageField = 'totalPage',
  } = options;

  // Helper to get nested property by path string
  const getNestedValue = (obj, path) => path.split('.').reduce((current, prop) => current?.[prop], obj);

  return {
    /**
     * Set list data from API response
     * @param {Object} state - Vuex state
     * @param {Object} param - { payload: API response object }
     */
    setData(state, { payload }) {
      state.allData = getNestedValue(payload, dataPath) || [];
    },

    /**
     * Clear list data and reset counters
     * @param {Object} state - Vuex state
     */
    clearData(state) {
      state.allData = [];
      state.totalData = 0;
    },

    /**
     * Set total data and total page count
     * @param {Object} state - Vuex state
     * @param {Object} param - { payload: API response object }
     */
    setTotalData(state, { payload }) {
      state.totalData = parseInt(getNestedValue(payload, `data.${countField}`), 10) || 0;
      state.totalPage = parseInt(getNestedValue(payload, `data.${pageField}`), 10) || 0;
    },

    /**
     * Set loading state
     * @param {Object} state - Vuex state
     * @param {Boolean} loading - Loading status
     */
    setLoading(state, loading) {
      state.loading = loading;
    },

    /**
     * Set request/cancelation data
     * @param {Object} state - Vuex state
     * @param {*} payload - Data to store
     */
    setCancelation(state, payload) {
      state.requestData = payload;
    },
  };
}

/**
 * Create standard list state for a store module
 * @returns {Object} Initial state with default list properties
 */
export function createListState() {
  return () => ({
    allData: [],
    totalData: 0,
    loading: false,
    totalPage: 0,
    requestData: null,
  });
}
